import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useRestoreSettings } from '../hooks/use-restore-settings.js';
import { ALL_SECTIONS } from '../constants.js';

function RestoreSettingsTab() {
  const {
    file,
    setFile,
    password,
    setPassword,
    strategy,
    setStrategy,
    sections,
    toggleSection,
    confirmOpen,
    openConfirm,
    closeConfirm,
    restore,
    isLoading,
    error,
    success,
  } = useRestoreSettings();

  const { t } = useTranslation();
  const fileInputRef = useRef(null);

  const SECTION_KEY_MAP = {
    settings_system: 'saveRestore.sectionGeneralSettings',
    settings_agreements: 'saveRestore.sectionAgreements',
    settings_email_credentials: 'saveRestore.sectionEmailCredentials',
    settings_bank_credentials: 'saveRestore.sectionBankCredentials',
    settings_crypto_credentials: 'saveRestore.sectionCryptoCredentials',
    settings_trading_servers: 'saveRestore.sectionTradingServers',
    settings_trading_accounts: 'saveRestore.sectionTradingAccounts',
    settings_trading_platform_alias: 'saveRestore.sectionTradingPlatformAlias',
    jurisdictions: 'saveRestore.sectionJurisdictions',
    kyc_steps: 'saveRestore.sectionKycSteps',
    kyc_fields: 'saveRestore.sectionKycFields',
    kyc_field_dependencies: 'saveRestore.sectionKycFieldDependencies',
    email_templates: 'saveRestore.sectionEmailTemplates',
    email_template_wraps: 'saveRestore.sectionEmailTemplateWraps',
  };

  const isValid = file && password && sections.length > 0;

  return (
    <>
      <div className="row">
        <div className="col-md-6">
          <h5 className="mb-3">{t('saveRestore.backupFile')}</h5>
          <div className="mb-3">
            <label className="form-label" htmlFor="restore-file">
              {t('saveRestore.selectFile')}
            </label>
            <input
              id="restore-file"
              ref={fileInputRef}
              type="file"
              className="form-control"
              accept=".crmbackup"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </div>
          <div className="mb-3">
            <label className="form-label" htmlFor="restore-password">
              {t('saveRestore.decryptionPassword')}
            </label>
            <input
              id="restore-password"
              type="password"
              className="form-control"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
          </div>

          <h5 className="mt-4 mb-3">{t('saveRestore.restoreStrategy')}</h5>
          <div className="mb-2">
            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                id="strategy-overwrite"
                name="strategy"
                value="overwrite"
                checked={strategy === 'overwrite'}
                onChange={() => setStrategy('overwrite')}
              />
              <label className="form-check-label" htmlFor="strategy-overwrite">
                <strong>{t('saveRestore.strategyOverwrite')}</strong>
                <div className="text-muted small">
                  {t('saveRestore.strategyOverwriteHint')}
                </div>
              </label>
            </div>
          </div>
          <div className="mb-3">
            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                id="strategy-merge"
                name="strategy"
                value="merge"
                checked={strategy === 'merge'}
                onChange={() => setStrategy('merge')}
              />
              <label className="form-check-label" htmlFor="strategy-merge">
                <strong>{t('saveRestore.strategyMerge')}</strong>
                <div className="text-muted small">
                  {t('saveRestore.strategyMergeHint')}
                </div>
              </label>
            </div>
          </div>
        </div>

        <div className="col-md-6">
          <h5 className="mb-3">
            {t('saveRestore.sectionsToRestore')}
            <span className="badge bg-secondary ms-2">{sections.length} / {ALL_SECTIONS.length}</span>
          </h5>
          <div className="mb-2">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary me-2"
              onClick={() => ALL_SECTIONS.forEach((s) => !sections.includes(s.value) && toggleSection(s.value))}
            >
              {t('saveRestore.selectAll')}
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => sections.slice().forEach((v) => toggleSection(v))}
            >
              {t('saveRestore.deselectAll')}
            </button>
          </div>
          <div className="list-group">
            {ALL_SECTIONS.map((s) => (
              <label key={s.value} className="list-group-item list-group-item-action py-2 cursor-pointer">
                <input
                  className="form-check-input me-2"
                  type="checkbox"
                  checked={sections.includes(s.value)}
                  onChange={() => toggleSection(s.value)}
                />
                {SECTION_KEY_MAP[s.value] ? t(SECTION_KEY_MAP[s.value]) : s.label}
              </label>
            ))}
          </div>
        </div>
      </div>

      {error && <div className="alert alert-danger mt-3 py-2">{error}</div>}
      {success && <div className="alert alert-success mt-3 py-2">{success}</div>}

      <div className="mt-4">
        <button
          type="button"
          className="btn btn-warning"
          disabled={isLoading || !isValid}
          onClick={openConfirm}
        >
          <i className="ti ti-restore me-2" />
          {t('saveRestore.restoreSettings')}
        </button>
      </div>

      {confirmOpen && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t('saveRestore.confirmRestoreTitle')}</h5>
                <button type="button" className="btn-close" onClick={closeConfirm} disabled={isLoading} />
              </div>
              <div className="modal-body">
                <p>
                  {t(sections.length !== 1 ? 'saveRestore.confirmRestoreBody_plural' : 'saveRestore.confirmRestoreBody', { count: sections.length, strategy })}
                </p>
                {strategy === 'overwrite' && (
                  <div className="alert alert-warning py-2">
                    <i className="ti ti-alert-triangle me-2" />
                    {t('saveRestore.overwriteWarning')}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeConfirm}
                  disabled={isLoading}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={restore}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      {t('saveRestore.restoring')}
                    </>
                  ) : (
                    t('saveRestore.confirmRestore')
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default RestoreSettingsTab;
